import React from "react";
import AccordionCard from "../../../../../../components/common/PL/AccordionCard/AccordionCard";
import BundleKey from "../../../../../../translation/bundleKey";
import FormSelect from "../../../../../../components/common/PL/Select/FormSelect";
import FormTextInput from "../../../../../../components/common/PL/TextInput/FormTextInput";
import FormDatePicker from "../../../../../../components/common/PL/DatePicker/FormDatePicker";
import FormDocumentPreviewForm from "../../../../../../components/common/PL/PicturePreview/FormDocumentPreviewForm";
import VehicleContentCompanionDataForm from "../../AccessVehicleForm/VehicleContentForm/VehicleContentCompanionDataForm/VehicleContentCompanionDataForm";
import { RegistrationMode } from "../../../registroIngresoNavigation";
import { validateNotExpired } from "../../../utils/registroIngresoSchema";
import useArtCompanies from "../../../../../../hooks/useArtCompanies";
import { useFormContext } from "react-hook-form";
import { EntryRegistrationFormValues } from "../../types";
import { resolveEmployeeNumber } from "./transportEmployeeNumberUtils";

type TransportContentDriverFormProps = {
  disabled?: boolean;
  mode?: RegistrationMode;
};

const TransportContentDriverForm: React.FC<TransportContentDriverFormProps> = ({
  disabled = false,
  mode,
}) => {
  const { companies: artCompanyOptions } = useArtCompanies();
  const { watch, setValue, getValues } = useFormContext<EntryRegistrationFormValues>();
  const driverArtCompany = watch("driverArtCompany");
  const transportEmpresaId = watch("transportEmpresa")?.id;
  const transportEmployeeNumbers = watch("transportEmployeeNumbers");

  // Los usuarios viejos traen la ART como texto libre (`artCompanyName`) en vez del id del
  // catálogo. Si lo prefilleado no es un id conocido, lo resolvemos por nombre para que el
  // desplegable lo muestre seleccionado.
  React.useEffect(() => {
    if (!driverArtCompany || !artCompanyOptions.length) return;
    if (artCompanyOptions.some((option) => option.value === driverArtCompany)) return;

    const byName = artCompanyOptions.find(
      (option) => option.label.trim().toLowerCase() === driverArtCompany.trim().toLowerCase()
    );
    setValue("driverArtCompany", byName ? String(byName.value) : "");
  }, [driverArtCompany, artCompanyOptions, setValue]);

  // El legajo se guarda por empresa, así que recién se puede resolver cuando el guardia elige una.
  // Se limpia si en la nueva empresa el chofer no tiene legajo: mandarlo igual lo guardaría mal.
  const autofilledLegajoRef = React.useRef("");
  React.useEffect(() => {
    const resolved = resolveEmployeeNumber(transportEmployeeNumbers, transportEmpresaId);
    const current = getValues("driverLegajoNumber") || "";
    // Lo que tipeó el guardia gana. Se puede pisar lo vacío, lo que escribió este mismo efecto y el
    // legajo suelto que prefilleó la búsqueda (legacy): ese no lo escribió el guardia y el valor por
    // empresa es más específico.
    const prefilled = getValues("old")?.transportEmployeeNumber || "";
    if (current && current !== autofilledLegajoRef.current && current !== prefilled) return;
    if (current === resolved) return;

    autofilledLegajoRef.current = resolved;
    setValue("driverLegajoNumber", resolved);
    // Baseline: sin esto el submit reenviaría por updateUser el legajo que acaba de leer.
    setValue("old", { ...(getValues("old") || {}), transportEmployeeNumber: resolved });
  }, [transportEmpresaId, transportEmployeeNumbers, getValues, setValue]);

  return (
    <div style={{ marginTop: "24px", marginBottom: "24px" }}>
      <AccordionCard
        id="transport-driver-section"
        titleKey={BundleKey.ACCESS_TYPE_TRANSPORT_DRIVER_TITLE}
        expanded
      >
        {/* Legajo y licencia del chofer. */}
        <div className="transportContentGrid">
          <FormTextInput
            name="driverLegajoNumber"
            label="Número de legajo"
            placeholder="-completar-"
            extraType="numeric"
            disabled={disabled}
          />
          <FormDatePicker
            name="driverLicenseExpirationDate"
            label="Fecha de Vencimiento de Licencia *"
            placeholder="-completar-"
            variant="pl"
            rules={{
              required: "La fecha de vencimiento es obligatoria",
              validate: validateNotExpired,
            }}
            disabled={disabled}
          />
          <FormDocumentPreviewForm
            name="driverLicensePhoto"
            label="Foto de licencia"
            compact
            displayVariant="file"
            readOnly={disabled}
          />
        </div>

        {/* La ART va en su propia grilla para que sus cuatro campos queden juntos y no intercalados
            con los de la licencia. */}
        <div className="transportContentGrid transportContentGridBlock">
          <FormSelect
            name="driverArtCompany"
            options={artCompanyOptions}
            label="Compañía de ART *"
            placeholder="-completar-"
            rules={{ required: "La compañía de ART es obligatoria" }}
            disabled={disabled}
            isSearchable
            focusFirstOptionOnOpen
          />
          <FormTextInput
            name="driverArtPolicyNumber"
            label="Número de Póliza ART *"
            placeholder="-completar-"
            rules={{ required: "El número de póliza es obligatorio" }}
            disabled={disabled}
          />
          <FormDatePicker
            name="driverArtExpirationDate"
            label="Fecha de Vencimiento ART *"
            placeholder="-completar-"
            variant="pl"
            rules={{
              required: "La fecha de vencimiento es obligatoria",
              validate: validateNotExpired,
            }}
            disabled={disabled}
          />
          <FormDocumentPreviewForm
            name="driverArtPhoto"
            label="Foto de póliza ART"
            compact
            displayVariant="file"
            readOnly={disabled}
          />
        </div>

        <VehicleContentCompanionDataForm disabled={disabled} mode={mode} />
      </AccordionCard>
    </div>
  );
};

export default TransportContentDriverForm;
